import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { ThreadMessage } from '../types';
import { batchMarkThreadMessagesRead } from '../services/firestoreService';

interface UseThreadMessagesResult {
  messages: ThreadMessage[];
  loading: boolean;
  unreadCount: number;
}

/**
 * Real-time listener for thread messages on a specific submission.
 * Automatically marks incoming messages as read for the current user's role.
 */
export function useThreadMessages(
  submissionId: string | null,
  currentRole: 'researcher' | 'participant' | null,
  organizationId?: string | null,
): UseThreadMessagesResult {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!submissionId || !currentRole || !organizationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'threadMessages'),
      where('submissionId', '==', submissionId),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'asc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ThreadMessage));
        setMessages(msgs);
        setLoading(false);

        // Auto-mark unread messages from the other party as read
        const oppositeRole = currentRole === 'researcher' ? 'participant' : 'researcher';
        const unreadIds = msgs
          .filter(m => m.senderRole === oppositeRole && !m.readByRecipient)
          .map(m => m.id);

        if (unreadIds.length > 0) {
          batchMarkThreadMessagesRead(unreadIds).catch(() => {});
        }
      },
      (err) => console.error('[FS_DENY:useThreadMessages]', { submissionId, organizationId, err }),
    );

    return () => unsubscribe();
  }, [submissionId, currentRole, organizationId]);

  const oppositeRole = currentRole === 'researcher' ? 'participant' : 'researcher';
  const unreadCount = messages.filter(m => m.senderRole === oppositeRole && !m.readByRecipient).length;

  return { messages, loading, unreadCount };
}
